<script setup lang="ts">
import { computed, ref, watch } from 'vue'

import type { InsightGuidance } from '../../service/insightGuidance'

import { useI18n } from '@/shared/i18n'
import { RichTextViewer } from '@/shared/ui/editor'

type GuidanceTab = 'actions' | 'risks'

const props = defineProps<{ guidance: InsightGuidance }>()

const { t } = useI18n()
const active = ref<GuidanceTab>('actions')

const tabs = computed(() =>
  (
    [
      { key: 'actions', label: t('insights.detail.actions'), items: props.guidance.actions },
      { key: 'risks', label: t('insights.detail.risks'), items: props.guidance.risks },
    ] as const
  ).filter((tab) => tab.items.length > 0),
)
const items = computed(() => tabs.value.find((tab) => tab.key === active.value)?.items ?? [])

watch(
  tabs,
  (next) => {
    if (!next.some((tab) => tab.key === active.value)) active.value = next[0]?.key ?? 'actions'
  },
  { immediate: true },
)
</script>

<template>
  <section
    data-pdf-section
    class="insight-guidance grid gap-sm"
    data-testid="insight-guidance"
    :aria-label="t('insights.detail.aiAnalysis')"
  >
    <small class="insight-guidance__label">{{ t('insights.detail.aiAnalysis') }}</small>

    <div
      class="insight-guidance__card grid gap-lg p-md border border-border rounded-2xl bg-surface"
    >
      <RichTextViewer
        v-if="guidance.explanation"
        class="insight-guidance__explanation"
        :content="guidance.explanation"
      />

      <div v-if="tabs.length" class="insight-guidance__panel grid gap-md">
        <div
          v-if="tabs.length > 1"
          class="insight-guidance__tabs flex flex-wrap gap-xs"
          role="tablist"
          :aria-label="t('insights.detail.aiAnalysis')"
        >
          <button
            v-for="tab in tabs"
            :key="tab.key"
            type="button"
            role="tab"
            class="insight-guidance__tab flex cursor-pointer items-center gap-2xs rounded-full border-0 px-md py-2xs text-xs font-semibold"
            :class="`insight-guidance__tab--${tab.key}`"
            :aria-selected="active === tab.key"
            :tabindex="active === tab.key ? 0 : -1"
            @click="active = tab.key"
            @keydown.right.prevent="active = tabs[(tabs.indexOf(tab) + 1) % tabs.length]!.key"
            @keydown.left.prevent="
              active = tabs[(tabs.indexOf(tab) - 1 + tabs.length) % tabs.length]!.key
            "
          >
            {{ tab.label }}
            <span class="insight-guidance__count rounded-full px-2xs py-3xs text-[0.625rem]">{{
              tab.items.length
            }}</span>
          </button>
        </div>

        <ul
          class="insight-guidance__list m-0 grid gap-xs p-0"
          :class="`is-${active}`"
          role="tabpanel"
        >
          <li
            v-for="(item, index) in items"
            :key="`${active}-${index}`"
            data-pdf-block
            class="insight-guidance__item flex items-start gap-sm rounded-lg px-md py-sm text-sm"
          >
            <span class="insight-guidance__dot shrink-0" />
            <span>{{ item }}</span>
          </li>
        </ul>
      </div>
    </div>
  </section>
</template>

<style scoped>
.insight-guidance__label {
  color: var(--ui-color-muted);
  font-size: 0.6875rem;
  font-weight: 600;
  text-transform: uppercase;
}
.insight-guidance__explanation {
  color: var(--ui-color-muted);
  font-size: 0.8125rem;
  line-height: 1.65;
}
.insight-guidance__tab {
  background: var(--ui-color-guidance-tab-bg);
  color: var(--ui-color-guidance-tab-fg);
}
.insight-guidance__tab[aria-selected='true'].insight-guidance__tab--actions {
  background: var(--ui-color-success-soft);
  color: var(--ui-color-guidance-action-fg);
}
.insight-guidance__tab[aria-selected='true'].insight-guidance__tab--risks {
  background: var(--ui-color-danger-soft);
  color: var(--ui-color-guidance-risk-fg);
}
.insight-guidance__count {
  background: color-mix(in srgb, var(--ui-color-surface) 90%, transparent);
}
.insight-guidance__list {
  list-style: none;
}
.insight-guidance__dot {
  width: 0.625rem;
  height: 0.625rem;
  margin-top: 0.25rem;
  border-radius: 50%;
}
.insight-guidance__list.is-actions .insight-guidance__item {
  background: var(--ui-color-success-soft);
}
.insight-guidance__list.is-actions .insight-guidance__dot {
  background: var(--ui-color-guidance-action-dot);
}
.insight-guidance__list.is-risks .insight-guidance__item {
  background: var(--ui-color-danger-soft);
}
.insight-guidance__list.is-risks .insight-guidance__dot {
  background: var(--ui-color-guidance-risk-dot);
}
</style>
